<template>
  <div class="main-faq sidebar-layout container critical-component-clear">
    <div class="sidebar-layout__grid grid">
      <div class="sidebar-layout__header col xs4 l3">
        <h2
          :id="`section-title-${liquid.sectionId}`"
          class="sidebar-layout__title text-heading-l"
          v-text="liquid.title"
        />

        <ul class="sidebar-layout__navigation">
          <li
            v-for="item in navigation"
            :key="item"
          >
            <a
              class="sidebar-layout__navigation-item text-heading-s"
              :class="{
                [$class('active')]: getIdForKey(item) === currentActiveLink
              }"
              :href="`#${getIdForKey(item)}`"
              @click="(event) => handleLinkClick(event, item)"
            >
              <icon-arrow-right />
              <span
                class="sidebar-layout__navigation-item-text"
                v-text="item"
              />
            </a>
          </li>
        </ul>

        <hr class="sidebar-layout__header-divider">

        <sidebar-layout-help
          class="sidebar-layout__help sidebar-layout__help--desktop"
          :liquid="liquid"
        />
      </div>

      <div
        ref="content"
        class="sidebar-layout__content grid grid--col-l9 col xs4 l4-12"
      >
        <div
          ref="accordionWrapper"
          class="main-faq__accordion-wrapper col xs4 l2-8"
        >
          <div
            v-for="(faq, key) of faqData"
            v-show="getIdForKey(key) === currentActiveLink"
            :key="key"
            class="main-faq__accordion-section"
            :data-id="getIdForKey(key)"
          >
            <h2
              class="main-faq__accordion-title text-heading-m"
              v-text="key"
            />

            <div class="main-faq__accordion-content">
              <accordion :panel-data="faq">
                <template
                  v-for="(faqItem, index) of faq"
                  :key="index"
                  #[`accordion-content-${index}`]
                >
                  <component
                    :is="faqItem.component"
                    v-if="faqItem.component"
                    v-bind="faqItem.props"
                  />
                </template>
              </accordion>
            </div>
          </div>
        </div>
      </div>

      <sidebar-layout-help
        class="sidebar-layout__help sidebar-layout__help--mobile col xs4"
        :liquid="liquid"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main faq (main-faq)
 * -----------------------------------------------------------------------------
 * A section for the main FAQ content.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */

import Accordion from "~async/accordion/accordion"
import FaqTable from '~async/main-faq/faq-table'
import SidebarLayoutHelp from '~async/sidebar-layout-help/sidebar-layout-help'
import IconArrowRight from '~icons/direction/arrow/right.svg'

import { convertToHandle } from '~/helpers/convert'
import { scrollToElement } from '~/helpers/general'

export default {
  name: 'MainFaq',

  components: {
    Accordion,
    FaqTable,
    SidebarLayoutHelp,
    IconArrowRight,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      currentActiveLink: null,
    }
  },

  computed: {

    /**
     * Compute faqData.
     * - Loops through the blocks prop and formats the data into an object.
     * @returns {Object} formatted FAQ data.
     */
    faqData() {
      const data = {}

      this.blocks.forEach((block) => {
        if (!block.category) {
          return
        }

        data[block.category] = data[block.category] || []

        /**
         * If no title (question) and FAQ image text type then add content to
         * the end of the previous block.
         */
        if (!block.title && block.type == 'faq_image_text') {
          const previousIndex = data[block.category].length - 1
          const previousBlock = data[block.category][previousIndex]

          if (previousBlock) {
            previousBlock.content += block.content
            data[block.category][previousIndex] = previousBlock
            return
          }
        }

        data[block.category].push(block)
      })

      return data
    },

    /**
     * Compute array of navigation items based on the `faqData`.
     * @returns {Array} list of navigation items.
     */
    navigation() {
      return Object.keys(this.faqData)
    },
  },

  watch: {

    /**
     * Watch changes to `currentActiveLink`
     * - Scrolls related element into view
     * - Updates URL history state
     * @param _newValue new `currentActiveLink` value
     * @param oldValue old `currentActiveLink` value
     */
    async currentActiveLink(_newValue, oldValue) {
      if (!oldValue) {
        return
      }

      await this.$nextTick()

      const accordionWrapper = this.$refs.accordionWrapper

      scrollToElement(accordionWrapper)

      const updatedUrl = new URL(window.location)
      updatedUrl.hash = this.currentActiveLink
      history.replaceState({}, '', updatedUrl.href)
    },
  },

  mounted() {
    this.setInitialActiveLink()
  },

  methods: {

    /**
     * Handleize string for use as an id.
     * @param {String} key category string
     * @returns {String} - handleized string
     */
    getIdForKey(key) {
      return convertToHandle(key)
    },

    /**
     * Set the initial active link data.
     * - Gets the current active tab from the url hash.
     * - Falls back to the first item.
     */
    setInitialActiveLink() {
      if (!this.navigation.length) {
        return
      }

      if (!window.location.hash) {
        this.currentActiveLink = convertToHandle(this.navigation[0])
        return
      }

      const activeLinkHandle = window.location.hash.replace('#', '')

      const isValidLinkHandle = this.navigation.some((item) => {
        return convertToHandle(item) === activeLinkHandle
      })

      if (!isValidLinkHandle) {
        this.currentActiveLink = convertToHandle(this.navigation[0])
        return
      }

      this.currentActiveLink = activeLinkHandle
    },

    /**
     * Handle navigation link click.
     * @param {Event} event - click event
     * @param {String} item - clicked item.
     */
    handleLinkClick(event, item) {
      event.preventDefault()

      const itemHandle = convertToHandle(item)

      this.currentActiveLink = itemHandle
    },
  },
}
</script>

<style lang="scss">
@import './main-faq';
</style>
